import posthog, {PostHogConfig, Properties, RequestResponse} from 'posthog-js';
import {User} from './FirestoreService';

class PostHogService {
  initialized = false;
  initPromise: Promise<void> | null = null;

  /**
   * Initializes PostHog. This should be called once at the start of the app.
   * @param apiKey The PostHog API key for the given project and environment
   * @param options Optional PostHog options
   */
  async init(apiKey: string, options: Partial<PostHogConfig> = {}): Promise<void> {
    if (this.initPromise) return this.initPromise;

    this.initPromise = new Promise<void>((resolve, reject) => {
      posthog.init(
        apiKey,
        {
          // PostHog's managed reverse proxy, so ad blockers don't drop events
          api_host: 'https://e.northpointkc.com',
          // Keeps the toolbar and "view in PostHog" links on PostHog's app now that api_host is our proxy
          ui_host: 'https://us.posthog.com',
          disable_session_recording: true,
          session_recording: {
            maskAllInputs: false,
            maskInputOptions: {
              password: true,
            },
          },
          loaded: () => {
            this.initialized = true;
            console.log('PostHog initialized');
            resolve();
          },
          on_request_error: (error: RequestResponse) => {
            console.error(error);
            reject(new Error(`PostHog request error: ${error.statusCode} ${error.text}`));
          },
          ...options,
        },
      );
    }).catch((error) => {
      error.message = `PostHog initialization failed: ${error.message}`;
      console.error(error);
      throw error;
    });

    return this.initPromise;
  }

  /**
   * Logs an event to PostHog
   * @param eventName Should be in the format of 'object_action', using snake case and present tense verbs
   *  Ex: 'competitive_building_create', 'overlay_delete'
   * @param eventProperties Optional event properties to be sent to PostHog
   */
  logEvent(eventName: string, eventProperties: Properties = {}) {
    try {
      if (!this.initialized) throw new Error('PostHog is not initialized. Please call init() first.');
      posthog.capture(eventName, eventProperties);
    } catch (err) {
      err.message = `PostHog logEvent failed: ${err.message}`;
      console.error(err);
    }
  }

  /**
   * Identifies a user with PostHog if the user is logged in. Once a user is identified, all events will be
   * associated with that user.
   * @param userId The user ID of the logged in user
   * @param userProperties Optional user properties to be sent to PostHog
   */
  identifyUser(user: User, userProperties: Properties = {}) {
    try {
      if (!this.initialized) throw new Error('PostHog is not initialized. Please call init() first.');
      if (!user || !user.uid) throw new Error('User is not logged in');

      posthog.identify(user.uid, {email: user.email, name: user.displayName, ...userProperties});
    } catch (err) {
      err.message = `PostHog identifyUser failed: ${err.message}`;
      console.error(err);
    }
  }

  /**
   * Starts session recording for the current user.
   */
  startSessionRecording() {
    try {
      if (!this.initialized) throw new Error('PostHog is not initialized. Please call init() first.');
      posthog.startSessionRecording();
      console.log('Started Posthog session recording');
    } catch (err) {
      err.message = `PostHog startSessionRecording failed: ${err.message}`;
      console.error(err);
    }
  }

  /**
   * Logs an error to PostHog
   * @param error The error object to be logged
   * @param errorInfo Optional additional error information to be sent to PostHog
   */
  logError(error: Error, errorInfo: Properties = {}) {
    try {
      if (!this.initialized) throw new Error('PostHog is not initialized. Please call init() first.');
      posthog.captureException(error, errorInfo);
    } catch (err) {
      err.message = `PostHog logError failed: ${err.message}`;
      console.error(err);
    }
  }
}

let service: PostHogService;
const initialize = async (options: Partial<PostHogConfig> = {}) => {
  try {
    if (service?.initialized) {
      console.warn('PostHog already initialized. Skipping initialization.');
      return;
    }
    const apiKey = process.env.VUE_APP_POSTHOG_API_KEY;
    if (!apiKey) {
      console.warn(`PostHog API key not set for the ${process.env.NODE_ENV} environment. Skipping initialization.`);
      return;
    }

    service = new PostHogService();
    await service.init(apiKey, options);
  } catch (err) {
    err.message = `PostHog initialize failed: ${err.message}`;
    console.error(err);
  }
};

export {
  service as PostHogService,
  initialize,
};
